<template>
  <v-card variant="outlined" class="mb-2">
    <v-card-text class="pa-3">
      <div class="d-flex justify-space-between align-center mb-1">
        <router-link
          :to="getAccountRoute({ name: 'deal', params: { id: item._key } })"
          class="text-decoration-none"
        >
          <strong class="text-primary text-body-1">{{ item.name }}</strong>
        </router-link>
      </div>
      <v-list density="compact" class="pa-0">
        <v-list-item v-if="item.contract_pending" class="px-0 py-1">
          <div class="py-0">
            <CommonChip class="mt-1 text-white" color="orange">Contract Pending</CommonChip>
          </div>
        </v-list-item>
        <v-list-item class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              {{ $t(LocalizationEnum.transaction_type) }}:
              <span style="text-transform: capitalize"> {{ getDealTransactionTypeLabel(item) }}</span>
            </v-list-item-title>
          </div>
        </v-list-item>
        <v-list-item class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              Category:
              <Label :value="item.category" />
            </v-list-item-title>
          </div>
        </v-list-item>
        <v-list-item v-if="item.suite" class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              Suite #:
              <Label :value="item.suite" />
            </v-list-item-title>
          </div>
        </v-list-item>
        <v-list-item v-if="isBuildingSizeInObject" class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              Space Size:
              <BuildingSizeLabel :value="buildingSize" />
            </v-list-item-title>
          </div>
        </v-list-item>
        <v-list-item v-if="isLandSizeInObject" class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              Land Size:
              <BuildingSizeLabel :value="landSize" />
            </v-list-item-title>
          </div>
        </v-list-item>
        <v-list-item v-if="isTotalConsiderationInObject" class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              Consideration: {{ $filters.formatCurrency(totalConsideration) }}
            </v-list-item-title>
          </div>
        </v-list-item>
        <v-list-item v-if="isTotalCommissionInObject" class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              Commission: {{ $filters.formatCurrency(totalCommission) }}
            </v-list-item-title>
          </div>
        </v-list-item>
        <v-list-item class="px-0 py-1">
          <div class="py-0">
            <v-list-item-title>
              Transaction Date: {{ formattedTransactionDate }}
            </v-list-item-title>
          </div>
        </v-list-item>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { IAiDealResult } from '@/bundles/AiAgent/types';
import { getDealTransactionTypeLabel } from '@/bundles/Deal/helpers/transaction/getDealTransactionLabel';
import { formatJSDate, formatNumber } from '@/utils/filters';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import Label from '@/bundles/Common/components/Label.vue';
import BuildingSizeLabel from '@/bundles/MeasurementUnits/components/BuildingSizeLabel.vue';

export default defineComponent({
  name: 'DealCard',

  components: {
    CommonChip,
    Label,
    BuildingSizeLabel,
  },
  mixins: [AccountLayoutMixin],

  props: {
    item: {
      type: Object as PropType<IAiDealResult>,
      required: true,
    },
  },

  setup () {
    return { LocalizationEnum };
  },

  computed: {
    isBuildingSizeInObject: function () {
      return this.item.transaction && 'total_square_feet' in this.item.transaction;
    },
    buildingSize: function () {
      return formatNumber(this.item.transaction?.total_square_feet);
    },
    isLandSizeInObject: function () {
      return this.item.transaction && 'total_acres' in this.item.transaction;
    },
    landSize: function () {
      return this.item.transaction?.total_acres || 0;
    },
    isTotalConsiderationInObject: function () {
      return this.item.transaction && 'consideration' in this.item.transaction;
    },
    totalConsideration: function () {
      return this.item.transaction?.consideration || null;
    },
    isTotalCommissionInObject: function () {
      return this.item.transaction && 'total_commission' in this.item.transaction;
    },
    totalCommission: function () {
      return this.item.transaction?.total_commission || null;
    },
    formattedTransactionDate () {
      const date = this.item.transaction?.transaction_date || this.item.transaction_date;
      return date ? formatJSDate(date) : '';
    },
  },

  methods: {
    getDealTransactionTypeLabel,
  }
});
</script>
